import styled from '@emotion/styled'
import { MappedName, TopRevenuesByDim } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { FunctionComponent, useEffect, useMemo, useRef } from 'react'
import { Chart, ChartWrapperOptions, ReactGoogleChartEvent } from 'react-google-charts'
import { Legend } from '~/components/Legend'
import { Widget, WidgetProps } from '~/components/Widget'
import { AnalyticsDefaultChartOptions, ChartWrapper, Legends } from '~/modules/analytics/AnalyticsCharts'
import { adxImpressionRevenue, allImpressionRevenue, asPercentage, hbImpressionRevenue, openBiddingImpressionRevenue, serverSideImpressionRevenue } from '~/modules/analytics/common/formulas'
import { getChartTooltip } from '~/modules/analytics/overview/revenue/PureTopRevenueByDimTooltip'
import { WithClassName } from '~/types/utils'
import { ANALYTICS_CHART_COLOR_CONFIG } from '~/utils/analytics'
import { displayWithCurrency } from '~/utils/string'
import { RevenueDataConfig } from './PureTopRevenueByDim.utils'

const ChartContainer = styled.div`
  flex: 1 1 auto;

  & > div {
    height: 230px;
  }
`

const WidgetTitle = styled.div`
  span {
    font-weight: bold;
  }
`

const getChartDataConfig = (rawData: TopRevenuesByDim): RevenueDataConfig[] => {
  const configs = [
    {
      name: ANALYTICS_CHART_COLOR_CONFIG['hb'].title,
      color: ANALYTICS_CHART_COLOR_CONFIG['hb'].color,
      formula: hbImpressionRevenue,
    },
    {
      name: ANALYTICS_CHART_COLOR_CONFIG['adx'].title,
      color: ANALYTICS_CHART_COLOR_CONFIG['adx'].color,
      formula: adxImpressionRevenue,
    },
    {
      name: ANALYTICS_CHART_COLOR_CONFIG['openBidding'].title,
      color: ANALYTICS_CHART_COLOR_CONFIG['openBidding'].color,
      formula: openBiddingImpressionRevenue,
    },
    {
      name: ANALYTICS_CHART_COLOR_CONFIG['prebidServer'].title,
      color: ANALYTICS_CHART_COLOR_CONFIG['prebidServer'].color,
      formula: serverSideImpressionRevenue,
    },
  ]

  return configs.filter((dataConfig) => dataConfig.formula.isComputable(rawData))
}

const computeOthersData = ({
  rawData,
  revenueDisplayThreshold,
  currencySymbol,
  chartDataConfig,
}: {
  rawData: TopRevenuesByDim
  revenueDisplayThreshold: number
  currencySymbol: CurrencySymbol
  chartDataConfig: RevenueDataConfig[]
}) => {
  let revenueSum = 0
  let otherSum = 0

  const data = chartDataConfig.map((dc) => {
    const typedData = rawData as Required<TopRevenuesByDim>
    const value = dc.formula.others(typedData)
    otherSum += value
    revenueSum += dc.formula.sum(typedData)
    return value
  })

  if (otherSum <= 0 || otherSum / revenueSum <= revenueDisplayThreshold) {
    return []
  }

  const allRevenuePercentage = asPercentage(allImpressionRevenue.percentage(otherSum, rawData as Required<TopRevenuesByDim>) * 100)
  const allRevenueDisplayable = `${allImpressionRevenue.displayable(otherSum)}`

  const details = chartDataConfig
    .map((conf, index) => ({
      name: conf.name,
      color: conf.color,
      displayable: `${conf.formula.displayable(data[index])}`,
      percentage: asPercentage((data[index] / otherSum) * 100),
    }))
    .filter((_, index) => data[index] > revenueDisplayThreshold)

  const otherData = [
    'Others',
    ...chartDataConfig.flatMap((dc, index) => {
      const value = data[index]
      return [
        value > 0 ? value : Number.NaN,
        dc.color,
        getChartTooltip({
          name: { value: '', label: 'Others' },
          currencySymbol,
          details,
          allImpressionRevenue: {
            name: allImpressionRevenue.name,
            percentage: allRevenuePercentage,
            displayable: allRevenueDisplayable,
          },
        }),
      ]
    }),
  ]

  return [otherData]
}

const computeChartData = ({ rawData, revenueDisplayThreshold, currencySymbol }: { rawData: TopRevenuesByDim; revenueDisplayThreshold: number; currencySymbol: CurrencySymbol }) => {
  const chartDataConfig = getChartDataConfig(rawData)

  const isAllRevenueComputable = allImpressionRevenue.isComputable(rawData)

  const filterUnderThreshold = (_: unknown, index: number) => {
    if (!isAllRevenueComputable) return false
    return allImpressionRevenue.percentage(allImpressionRevenue.compute(rawData, index), rawData) > revenueDisplayThreshold
  }

  const chartNames = rawData.name.filter(filterUnderThreshold)

  const typedRawData = rawData as Required<TopRevenuesByDim>

  const chartData = rawData.name
    .map((name, index) => {
      const allRevenue = allImpressionRevenue.compute(typedRawData, index)
      const allRevenuePercentage = asPercentage(allImpressionRevenue.percentage(allRevenue, typedRawData) * 100)
      const allRevenueDisplayable = `${allImpressionRevenue.displayable(allRevenue)}`

      const details = chartDataConfig
        .filter((dc) => dc.formula.compute(typedRawData, index) > 0)
        .map((dc) => ({
          name: dc.name,
          color: dc.color,
          displayable: `${dc.formula.displayable(dc.formula.compute(typedRawData, index))}`,
          percentage: asPercentage((dc.formula.compute(typedRawData, index) / allRevenue) * 100),
        }))

      return [
        name.label,
        ...chartDataConfig.flatMap((dc) => {
          const value = dc.formula.compute(typedRawData, index)
          return [
            value > 0 ? value : Number.NaN,
            dc.color,
            getChartTooltip({
              name,
              currencySymbol,
              details,
              allImpressionRevenue: {
                name: allImpressionRevenue.name,
                percentage: allRevenuePercentage,
                displayable: allRevenueDisplayable,
              },
            }),
          ]
        }),
      ]
    })
    .filter(filterUnderThreshold)

  const othersData = computeOthersData({ rawData, revenueDisplayThreshold, currencySymbol, chartDataConfig })

  return {
    chartData: [['label', ...chartDataConfig.flatMap((dc) => [dc.formula.name, { role: 'style' }, { type: 'string', role: 'tooltip', p: { html: true } }])], ...chartData, ...othersData],
    selectableNames: chartNames,
  }
}

const getChartLegend = (rawData: TopRevenuesByDim) => {
  const chartDataConfig = getChartDataConfig(rawData)
  return chartDataConfig.map((dataConfig, index) => <Legend key={`${dataConfig.name}-${index}`} iconColor={dataConfig.color} label={dataConfig.name} />)
}

const useChartEvents = (onValueSelect: (name: MappedName) => void, selectableNames: MappedName[]) => {
  const selectableNamesRef = useRef(selectableNames)

  useEffect(() => {
    selectableNamesRef.current = selectableNames
  }, [selectableNames])

  const chartEvents: ReactGoogleChartEvent[] = useMemo(
    () => [
      {
        eventName: 'select',
        callback: ({ chartWrapper }) => {
          const selection = chartWrapper?.getChart()?.getSelection()
          const row = selection && selection.length > 0 ? selection[0].row : undefined
          if (typeof row === 'number' && row < selectableNamesRef.current.length) {
            const data = selectableNamesRef.current[row]
            onValueSelect(data)
          }
        },
      },
    ],
    [onValueSelect]
  )

  return chartEvents
}

type PureOverviewRevenueProps = WithClassName &
  Omit<WidgetProps & { hideHeader?: false }, 'refreshTrackingLabel'> & {
    rawData: TopRevenuesByDim
    currencySymbol: CurrencySymbol
    revenueDisplayThreshold: number
    onValueSelect: (name: MappedName) => void
  }

export const PureTopRevenueByDim: FunctionComponent<PureOverviewRevenueProps> = ({ rawData, currencySymbol, revenueDisplayThreshold = 0.01, title, onValueSelect, ...props }) => {
  const chartOptions: ChartWrapperOptions['options'] = useMemo(
    () => ({
      ...AnalyticsDefaultChartOptions,
      chartArea: {
        ...AnalyticsDefaultChartOptions.chartArea,
        left: 100,
        right: 20,
        bottom: 20,
        top: 20,
      },
      hAxis: {
        ...AnalyticsDefaultChartOptions.hAxis,
        minValue: 0,
        gridlines: {
          ...AnalyticsDefaultChartOptions.hAxis?.gridLines,
          count: 3,
        },
        format: displayWithCurrency('#.##', currencySymbol),
      },
      vAxis: {
        ...AnalyticsDefaultChartOptions.vAxis,
        gridlines: { color: 'none' },
      },
      focusTarget: undefined,
      animation: {
        ...AnalyticsDefaultChartOptions.animation,
        easing: 'inAndOut',
      },
    }),
    [currencySymbol]
  )

  const { chartData, selectableNames } = useMemo(() => computeChartData({ rawData, revenueDisplayThreshold, currencySymbol }), [rawData, revenueDisplayThreshold, currencySymbol])

  const chartDataLegend = useMemo(() => getChartLegend(rawData), [rawData])

  const chartEvents = useChartEvents(onValueSelect, selectableNames)

  const widgetTitle = useMemo(
    () => (
      <WidgetTitle>
        Revenue per <span>{title}</span>
      </WidgetTitle>
    ),
    [title]
  )

  return (
    <Widget {...props} title={widgetTitle} refreshTrackingLabel={'ana-overview-revenue-refresh'}>
      <ChartContainer>
        <ChartWrapper>{chartData.length > 1 && <Chart chartType={'BarChart'} options={chartOptions} width={'100%'} height={'100%'} data={chartData} chartEvents={chartEvents} />}</ChartWrapper>
      </ChartContainer>
      <Legends>{chartDataLegend}</Legends>
    </Widget>
  )
}
